import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import api from "@/lib/api";

// Post-signup onboarding-steg: låt nya admins bjuda in sitt team direkt så
// att prövoperioden faktiskt används. Rendas efter Stripe Checkout success
// men är också åtkomlig från navigeringen om admin vill komma tillbaka.
export default function Welcome() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { companyName } = useBranding();

  const [rows, setRows] = useState([
    { name: "", email: "" },
    { name: "", email: "" },
    { name: "", email: "" },
  ]);
  const [sending, setSending] = useState(false);
  const [invites, setInvites] = useState([]); // {email, name, invite_url}

  // Om icke-admin — bara skicka till appen (welcome-flödet är för admin).
  if (user && user.role !== "admin") {
    navigate("/", { replace: true });
    return null;
  }

  const updateRow = (i, field, value) => {
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, [field]: value } : r)));
  };
  const addRow = () => setRows((rs) => [...rs, { name: "", email: "" }]);

  const send = async (e) => {
    e.preventDefault();
    const valid = rows.filter((r) => r.email.trim() && r.name.trim());
    if (valid.length === 0) {
      toast.error("Fyll i namn och e-post för minst en kollega");
      return;
    }

    setSending(true);
    const results = [];
    for (const r of valid) {
      try {
        const resp = await api.post("/users/invite", {
          email: r.email.trim().toLowerCase(),
          name: r.name.trim(),
          role: "employee",
        });
        // Backend kan returnera relativ URL — normalisera till absolut så
        // länken funkar när admin skickar den via SMS/e-post.
        const rawUrl = resp.data.invite_url || "";
        const invite_url = rawUrl.startsWith("http")
          ? rawUrl
          : `${window.location.origin}${rawUrl}`;
        results.push({ ...resp.data, invite_url, ok: true });
      } catch (err) {
        results.push({
          email: r.email,
          name: r.name,
          ok: false,
          error: err?.response?.data?.detail || "Kunde inte skapa inbjudan",
        });
      }
    }
    setSending(false);

    const okCount = results.filter((r) => r.ok).length;
    if (okCount > 0) {
      toast.success(
        `${okCount} inbjudan${okCount === 1 ? "" : "ar"} skapad${okCount === 1 ? "" : "e"} — kopiera och skicka länkarna nedan.`
      );
    }
    const failed = results.filter((r) => !r.ok);
    if (failed.length > 0) {
      toast.error(`${failed.length} kunde inte skapas`);
    }
    setInvites(results.filter((r) => r.ok));
  };

  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Länk kopierad");
    } catch {
      toast.error("Kunde inte kopiera — markera manuellt");
    }
  };

  return (
    <div data-testid="welcome-page" className="min-h-screen bg-app">
      <div className="max-w-3xl mx-auto px-6 py-14">
        <div className="mb-2 text-xs tracking-[0.2em] uppercase text-text-muted">
          {companyName} · Kom igång
        </div>
        <h1 className="font-heading text-4xl md:text-5xl tracking-tight text-text-primary leading-[1.05]">
          Bjud in ditt <span className="text-brand">team.</span>
        </h1>
        <p className="mt-4 text-text-muted text-sm max-w-xl">
          Gratis prövotid är igång — men värdet kommer först när kollegorna
          börjar stämpla sina timmar. Bjud in dem nu så är ni igång redan i
          morgon. Ni får personliga länkar att skicka via SMS, e-post eller chatt.
        </p>

        <form
          onSubmit={send}
          className="mt-10 bg-surface border border-app rounded-2xl p-6 md:p-8 space-y-4"
        >
          <div className="text-xs uppercase tracking-widest text-text-muted">
            Kollegor att bjuda in
          </div>
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input
                type="text"
                placeholder="Namn"
                value={r.name}
                onChange={(e) => updateRow(i, "name", e.target.value)}
                data-testid={`welcome-invite-name-${i}`}
                className="bg-app border border-app rounded-lg px-3 py-2 text-sm text-text-primary placeholder-text-muted focus:outline-none focus:border-brand"
              />
              <input
                type="email"
                placeholder="E-post"
                value={r.email}
                onChange={(e) => updateRow(i, "email", e.target.value)}
                data-testid={`welcome-invite-email-${i}`}
                className="bg-app border border-app rounded-lg px-3 py-2 text-sm text-text-primary placeholder-text-muted focus:outline-none focus:border-brand"
              />
            </div>
          ))}
          <button
            type="button"
            onClick={addRow}
            data-testid="welcome-add-row"
            className="text-sm text-brand hover:underline"
          >
            + Lägg till en till
          </button>

          <div className="pt-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
            <button
              type="submit"
              disabled={sending}
              data-testid="welcome-send-invites"
              className="tk-btn-primary px-6 py-3"
            >
              {sending ? "Skapar inbjudningar…" : "Skapa inbjudningslänkar"}
            </button>
            <Link
              to="/"
              className="text-sm text-text-muted hover:text-brand text-center sm:text-right"
              data-testid="welcome-skip-link"
            >
              Hoppa över tills senare →
            </Link>
          </div>
        </form>

        {invites.length > 0 && (
          <div
            data-testid="welcome-invites-list"
            className="mt-8 bg-surface border border-app rounded-2xl p-6 md:p-8"
          >
            <div className="text-xs uppercase tracking-widest text-text-muted">
              Skickas till kollegorna
            </div>
            <p className="mt-1 text-sm text-text-muted">
              Kopiera respektive länk och skicka via SMS, e-post eller chatt.
              Länken går ut om 14 dagar.
            </p>
            <div className="mt-5 space-y-3">
              {invites.map((inv, i) => (
                <div
                  key={i}
                  data-testid={`welcome-invite-row-${i}`}
                  className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center bg-app border border-app rounded-xl p-3"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-text-primary font-medium truncate">
                      {inv.name}
                    </div>
                    <div className="text-xs text-text-muted truncate">{inv.email}</div>
                  </div>
                  <input
                    readOnly
                    value={inv.invite_url}
                    onClick={(e) => e.target.select()}
                    className="flex-[2] bg-surface border border-app rounded-lg px-3 py-2 text-xs text-text-primary font-mono truncate"
                    data-testid={`welcome-invite-url-${i}`}
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => copy(inv.invite_url)}
                      data-testid={`welcome-invite-copy-${i}`}
                      className="tk-btn-primary text-xs px-3 py-2"
                    >
                      Kopiera
                    </button>
                    <a
                      href={`sms:?body=${encodeURIComponent(
                        `Hej ${inv.name}! Skapa ditt konto på ${companyName} tidrapportering här: ${inv.invite_url}`
                      )}`}
                      data-testid={`welcome-invite-sms-${i}`}
                      className="tk-btn-secondary text-xs px-3 py-2 text-center"
                    >
                      SMS
                    </a>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 flex justify-end">
              <Link to="/" className="tk-btn-primary" data-testid="welcome-done-link">
                Klar → gå till appen
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
